/**
 * 한 질문에 쓸 수 있는 예산.
 *
 * 예산이 세 곳에 흩어져 서로를 몰랐다 — 실행 루프의 세 라운드, 계획 안의 두 번 +
 * 완화 한 번, 계산 재시도 한 번. 최악이면 한 질문에 계획을 아홉 번 돈다. 사용자는
 * 그동안 화면을 보고 있다.
 *
 * 우리에게 진짜 한계는 횟수가 아니라 **시간**이다. 조회 한 번이 수십 초고 사업장
 * 순회는 열한 번이라 십오 분을 넘긴 적이 있다. 그래서 시간을 같이 센다.
 *
 * 실패한 시도도 쓴 것으로 센다. 안 그러면 계속 실패하는 길을 무한히 다시 걷는다.
 */
export type BudgetLimits = {
  /** 계획을 새로 세우는 횟수. */
  plans: number;
  /** 조회 호출 횟수. 사업장 순회는 한 번이 열한 번이다 — 그것도 여기서 센다. */
  retrievals: number;
  /** 이 질문에 쓸 수 있는 전체 시간. */
  millis: number;
};

export const DEFAULT_LIMITS: BudgetLimits = {
  plans: 4,
  retrievals: 24,
  millis: 15 * 60 * 1000,
};

export class Budget {
  private readonly startedAt: number;

  private readonly spent = { plans: 0, retrievals: 0 };

  constructor(
    private readonly limits: BudgetLimits = DEFAULT_LIMITS,
    now: () => number = () => Date.now(),
  ) {
    this.now = now;
    this.startedAt = now();
  }

  private readonly now: () => number;

  /** 쓴 것으로 센다. 성공했는지는 묻지 않는다 — 실패도 시간과 호출을 쓴다. */
  spend(what: keyof Budget['spent']): void {
    this.spent[what] += 1;
  }

  /** 아직 이 일을 더 할 수 있는가. */
  allows(what: keyof Budget['spent']): boolean {
    if (this.expired()) return false;
    return this.spent[what] < this.limits[what];
  }

  /** 시간이 다 됐는가. 횟수가 남아 있어도 여기서 멈춘다. */
  expired(): boolean {
    return this.now() - this.startedAt >= this.limits.millis;
  }

  /** 콘솔에 남길 한 줄. 무엇을 얼마나 썼는지 나중에 물을 수 있어야 한다. */
  describe(): string {
    const seconds = Math.round((this.now() - this.startedAt) / 1000);
    return `계획 ${this.spent.plans}/${this.limits.plans}, `
      + `조회 ${this.spent.retrievals}/${this.limits.retrievals}, ${seconds}초`;
  }
}
